import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getVehicleTrackingListForPlanning,
  getTransportersDropdownOptions,
  updateVehicleTrackingOnHold,
} from 'src/redux/Actions/VehicleTrackingOutward';
import 'src/style/vehicleTrackingPlanning.css';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import getColorCode from 'src/utils/GetThemeColor';
import { Col, Row, Modal } from 'react-bootstrap';
import FilterMultiSelect from 'src/utils/FilterMultiSelect';
import Breadcrumb from 'src/components/Breadcrumb';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import usePageTitle from 'src/utils/SetPageTitle';
import { useLocation, useNavigate } from 'react-router-dom';
import TruckXs from 'src/Images/icons/truck/truck-xs.png';
import TruckSm from 'src/Images/icons/truck/truck-sm.png';
import TruckMd from 'src/Images/icons/truck/truck-md.png';
import TruckLg from 'src/Images/icons/truck/truck-lg.png';
import TruckXl from 'src/Images/icons/truck/truck-xl.png';
import { ReactComponent as VehicleTrackingPlanningEmptyState } from 'src/Images/icons/vehicle-planning/vehicle-planning-empty-state.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import { ReactComponent as ExtraIcon } from 'src/Images/icons/sidebar/extra-sidebar-icon.svg';
import {
  getUnitOfMeasurements,
  getPlants,
  getVehicleTrackingWorkflows,
} from 'src/redux/Actions/VehicleTracking';
import { ACTIONS } from 'src/utils/roles/actions';
import { usePermissions } from 'src/utils/hooks/usePermissions';

export default function VehicleTrackingPlanningForOutward() {
  usePageTitle(`Vehicle Tracking Planning`);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchInputChange, setSearchInputChange] = useState('');
  const [searchInput, setSearchInput] = useState(null);
  const searchTexts = ['Search this list'];
  const [pageNumber, setPageNumber] = useState(1);
  const [openMenu, setOpenMenu] = useState(null);
  const [appliedFilterQueryString, setAppliedFilterQueryString] = useState('');
  const [isTareWeightCompleted, setIsTareWeightCompleted] = useState(true);
  const [filterFormData, setFilterFormData] = useState({
    transporterId: null,
    typeId: null,
    capacityFrom: null,
    capacityTo: null,
    unitId: null,
    plantId: null,
    workflowId: null,
    startDate: null,
    endDate: null,
  });

  const [transporterSearchTerm, setTransporterSearchTerm] = useState('');
  const [paginationBatchSize, setPaginationBatchSize] = useState(10);
  const [unitSearchTerm, setUnitSearchTerm] = useState('');

  const [showFunnel, setShowFunnel] = useState(false);

  const [onHoldModal, setOnHoldModal] = useState({
    show: false,
    vehicleTrackingNumber: null,
    isOnHold: false,
    reason: '',
    isSubmitting: false,
    reasonError: '',
  });

  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tareWeightStatusPillActiveStyle = {
    backgroundColor: getColorCode(themeColor),
    borderColor: getColorCode(themeColor),
  };

  const tenantOrganisationId = authReducer?.userData?.organisations?.id;

  // Use centralized permission hook
  const { hasPermission } = usePermissions();
  const hasEditPermission = hasPermission(ACTIONS.VEHICLE_PLANNING.EDIT);

  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );

  const VehicleTrackingOutwardReducer = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );

  const { unitOfMeasurements, plantsData, workflows } =
    vehicleTrackingDataReducer;

  const { vehicleTrackingListForPlanning, transporterList } =
    VehicleTrackingOutwardReducer;

  const { totalPages } = vehicleTrackingListForPlanning;

  const filterQueryString = useMemo(() => {
    const searchPayload = {
      transporter_id: filterFormData?.transporterId?.value || null,
      vehicle_type: filterFormData?.typeId?.value || null,
      capacity_start_range: filterFormData?.capacityFrom || null,
      capacity_end_range: filterFormData?.capacityTo || null,
      unit_of_measurement_id: filterFormData?.unitId?.value || null,
      plant_id: filterFormData?.plantId
        ? Array.isArray(filterFormData.plantId)
          ? filterFormData.plantId.map((plant) => plant.value)
          : filterFormData.plantId?.value
        : null,
      workflow_id: filterFormData?.workflowId?.value || null,
      start_date: filterFormData?.startDate || null,
      end_date: filterFormData?.endDate || null,
    };

    const params = new URLSearchParams();
    Object.entries(searchPayload).forEach(([key, value]) => {
      Array.isArray(value)
        ? value.forEach((item) => params.append(key, item))
        : value && params.append(key, value);
    });

    return params.toString();
  }, [filterFormData]);

  const planningQueryString = useMemo(() => {
    const params = new URLSearchParams(appliedFilterQueryString);
    params.set('is_tare_weight_completed', String(isTareWeightCompleted));
    return params.toString();
  }, [appliedFilterQueryString, isTareWeightCompleted]);

  useEffect(() => {
    dispatch(
      getVehicleTrackingListForPlanning(
        tenantOrganisationId,
        pageNumber,
        searchInput,
        paginationBatchSize,
        planningQueryString,
      ),
    );
  }, [
    dispatch,
    tenantOrganisationId,
    pageNumber,
    searchInput,
    paginationBatchSize,
    planningQueryString,
  ]);

  const transportersDataOption = transporterList?.map((transporterObj) => ({
    value: transporterObj.id,
    label: transporterObj.name,
  }));

  const vehicleTypeDataOptions = [
    { value: '1', label: 'Pickup Truck' },
    { value: '2', label: 'Mini Truck' },
    { value: '3', label: 'Stake Truck' },
    { value: '4', label: 'Tractor Trailer' },
    { value: '5', label: 'Container Truck' },
  ];

  const unitOfMeasurementOptions = unitOfMeasurements?.map((unitObj) => ({
    label: unitObj.name,
    value: unitObj.id,
  }));

  const plantDataOptions =
    plantsData?.map((plantObj) => ({
      value: plantObj.id,
      label: plantObj.name,
    })) || [];

  const workflowDataOptions =
    workflows?.map((workflowObj) => ({
      value: workflowObj.id,
      label: workflowObj.name,
    })) || [];

  const handleFilterInputChange = (event) => {
    const { name, value } = event.target;
    setFilterFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  function handleResetFilter() {
    setFilterFormData({
      transporterId: null,
      typeId: null,
      capacityFrom: null,
      capacityTo: null,
      unitId: null,
      plantId: null,
      workflowId: null,
      startDate: null,
      endDate: null,
    });
    setAppliedFilterQueryString('');
    setPageNumber(1);
  }

  function handleFilterSubmit() {
    setPageNumber(1);
    setSearchInput(null);
    setAppliedFilterQueryString(filterQueryString);
    setShowFunnel(false);
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setSearchInputChange(
        (prev) =>
          searchTexts[(searchTexts.indexOf(prev) + 1) % searchTexts.length],
      );
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;

  const handleFunnelToggle = () => {
    setShowFunnel(!showFunnel);
  };

  const handleSearchInput = (e) => {
    const { value } = e.target;
    setPageNumber(1);
    setSearchInput(value);
  };

  function handleTareWeightStatusChange(status) {
    setIsTareWeightCompleted(status);
    setPageNumber(1);
  }

  useSetBreadcrumbData(location.pathname, 'Vehicle Planning');

  useEffect(() => {
    function handleDocumentClick(event) {
      if (!event.target.closest('.vehicle-planning-card-menu')) {
        setOpenMenu(null);
      }
    }

    if (openMenu) {
      document.addEventListener('mousedown', handleDocumentClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [openMenu]);

  function handleMoveToCreateOutward() {
    navigate('/outward/create/');
  }

  function handleMoveToUpdatePlanning(vehicleTrackingNumber) {
    navigate(`/outward/create-planning/${vehicleTrackingNumber}`);
  }

  function handleMoveToCreateDuplicateVehicleTracking(vehicleTrackingData) {
    navigate(`/create-duplicate-vehicle-tracking/`, {
      state: {
        refVehicleTrackingData: vehicleTrackingData,
      },
    });
  }

  function handleOpenOnHoldModal(vehicleTrackingNumber, isOnHold) {
    setOnHoldModal({
      show: true,
      vehicleTrackingNumber,
      isOnHold,
      reason: '',
      isSubmitting: false,
      reasonError: '',
    });
  }

  function handleCloseOnHoldModal() {
    setOnHoldModal({
      show: false,
      vehicleTrackingNumber: null,
      isOnHold: false,
      reason: '',
      isSubmitting: false,
      reasonError: '',
    });
  }
  function handleOnHoldSubmit() {
    if (!onHoldModal.isOnHold && !onHoldModal.reason.trim()) {
      setOnHoldModal((prev) => ({
        ...prev,
        reasonError: 'Reason is required.',
      }));
      return;
    }
    setOnHoldModal((prev) => ({
      ...prev,
      isSubmitting: true,
      reasonError: '',
    }));
    dispatch(
      updateVehicleTrackingOnHold({
        tenant_organisation_id: tenantOrganisationId,
        vehicle_tracking_number: onHoldModal.vehicleTrackingNumber,
        is_on_hold: !onHoldModal.isOnHold,
        on_hold_reason: onHoldModal.reason || null,
      }),
    )
      .then(() => {
        handleCloseOnHoldModal();
        dispatch(
          getVehicleTrackingListForPlanning(
            tenantOrganisationId,
            pageNumber,
            searchInput,
            paginationBatchSize,
            planningQueryString,
          ),
        );
      })
      .catch(() => {
        setOnHoldModal((prev) => ({ ...prev, isSubmitting: false }));
      });
  }

  return (
    <div className="page-layout">
      <div className="div-space-between slider-padding left-padding">
        <div className="div-flex-row cg-5">
          <Breadcrumb />
        </div>
        <Col xxl={2} xl={2} lg={2} className="right-padding">
          <div className="input-wrapper">
            <input
              type="text"
              className="form-input-section input-icon-remove"
              placeholder={`${searchInputChange}`}
              value={searchInput || ''}
              onChange={handleSearchInput}
            />
            <span className="form-input-section-icon">
              <Search className="icon-size-lg" />
            </span>
          </div>
        </Col>
      </div>
      <div className="left-padding three-col-layout-header div-flex-row div-space-between right-padding div-align-center">
        <div className="text-xxxl text-bold">Vehicle Planning</div>
        <div className="div-flex-row">
          <div className="vehicle-planning-status-pill-container div-flex-row div-align-center cg-10 right-padding">
            <button
              type="button"
              className={`vehicle-planning-status-pill text-sm ${
                isTareWeightCompleted
                  ? 'vehicle-planning-status-pill-active'
                  : ''
              }`}
              style={
                isTareWeightCompleted
                  ? tareWeightStatusPillActiveStyle
                  : undefined
              }
              onClick={() => handleTareWeightStatusChange(true)}
            >
              Tare Completed
            </button>
            <button
              type="button"
              className={`vehicle-planning-status-pill text-sm ${
                !isTareWeightCompleted
                  ? 'vehicle-planning-status-pill-active'
                  : ''
              }`}
              style={
                !isTareWeightCompleted
                  ? tareWeightStatusPillActiveStyle
                  : undefined
              }
              onClick={() => handleTareWeightStatusChange(false)}
            >
              Tare Pending
            </button>
          </div>
          <div className="div-flex-row div-align-center cg-20 right-padding">
            <div className="div-flex-row cg-20 div-align-center">
              <button
                className="icon-btn-none"
                onClick={handleFunnelToggle}
                aria-label="Funnel"
              >
                <Funnel
                  className="icon-size-lg"
                  color={showFunnel ? getColorCode(themeColor) : '#9F9F9F'}
                />
              </button>
            </div>
            <select
              value={paginationBatchSize}
              onChange={(event) => {
                setPaginationBatchSize(Number(event.target.value));
                setPageNumber(1);
              }}
              className="filter-input-section"
            >
              {[10, 20, 30, 40, 50, 100, 500, 1000].map((size) => (
                <option value={size} key={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <Row className="g-0">
        <Col
          xxl={showFunnel ? 10 : 12}
          xl={showFunnel ? 9 : 12}
          lg={showFunnel ? 8 : 12}
          md={showFunnel ? 8 : 12}
          sm={showFunnel ? 0 : 12}
          xs={showFunnel ? 0 : 12}
          className="border-bottom"
        >
          <div
            className={`theme-scrollable-container-${themeColor} scrollable-container vehicle-planning-record-container top-padding`}
          >
            {vehicleTrackingListForPlanning?.vehicleTrackings?.length > 0 ? (
              <div className="div-flex-column rg-20 gap-5">
                {vehicleTrackingListForPlanning.vehicleTrackings.map(
                  (vehicleTrackingData) => {
                    let truckIcon;

                    switch (vehicleTrackingData.vehicle_type) {
                      case '1':
                        truckIcon = TruckXs;
                        break;
                      case '2':
                        truckIcon = TruckSm;
                        break;
                      case '3':
                        truckIcon = TruckMd;
                        break;
                      case '4':
                        truckIcon = TruckLg;
                        break;
                      default:
                        truckIcon = TruckXl;
                    }

                    const isVehicleEditable =
                      hasEditPermission && !vehicleTrackingData?.is_on_hold;
                    return (
                      <div
                        key={vehicleTrackingData.vehicle_tracking_number}
                        className="vehicle-planning-card-height vehicle-planning-card-container"
                      >
                        <div className="vehicle-planning-card-container-label div-flex-row cg-10">
                          <div className="vehicle-planning-pill-blue">
                            <span
                              className={`theme-text-color-${themeColor} text-sm`}
                            >
                              {vehicleTrackingData.serial_number}
                            </span>
                          </div>
                          <div className="vehicle-planning-pill-blue">
                            <span
                              className={`theme-text-color-${themeColor} text-sm`}
                            >
                              {vehicleTrackingData.vehicle_tracking_code ||
                                'N/A'}
                            </span>
                          </div>
                          {vehicleTrackingData?.is_on_hold && (
                            <div className="vehicle-planning-pill-red">
                              <span className="text-sm">On Hold</span>
                            </div>
                          )}
                        </div>

                        <div
                          className={`vehicle-planning-card-container-detail div-flex-column theme-border-hover-${themeColor}`}
                        >
                          {hasEditPermission && (
                            <div className="vehicle-planning-card-menu">
                              <ExtraIcon
                                onClick={() =>
                                  setOpenMenu(
                                    openMenu ===
                                      vehicleTrackingData?.vehicle_tracking_number
                                      ? null
                                      : vehicleTrackingData?.vehicle_tracking_number,
                                  )
                                }
                                aria-label="Vehicle options"
                                cursor="pointer"
                              />

                              {openMenu ===
                                vehicleTrackingData?.vehicle_tracking_number && (
                                <div className="action-dropdown-container">
                                  {vehicleTrackingData?.is_on_hold && (
                                    <div
                                      className="action-dropdown-item text-sm-outfit text-medium-bold"
                                      onClick={() => {
                                        handleMoveToCreateDuplicateVehicleTracking(
                                          vehicleTrackingData,
                                        );
                                        setOpenMenu(null);
                                      }}
                                      role="button"
                                      tabIndex={0}
                                    >
                                      Duplicate Vehicle Tracking
                                    </div>
                                  )}
                                  <div
                                    className="action-dropdown-item text-sm-outfit text-medium-bold"
                                    onClick={() => {
                                      handleOpenOnHoldModal(
                                        vehicleTrackingData?.vehicle_tracking_number,
                                        vehicleTrackingData?.is_on_hold,
                                      );
                                      setOpenMenu(null);
                                    }}
                                    role="button"
                                    tabIndex={0}
                                  >
                                    {vehicleTrackingData?.is_on_hold ? (
                                      <>Remove Hold</>
                                    ) : (
                                      <>Keep on Hold</>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          <Row className="m-0">
                            <Col
                              xxl={2}
                              xl={2}
                              lg={2}
                              md={2}
                              sm={2}
                              xs={2}
                              className="p-0"
                            >
                              <img
                                src={truckIcon}
                                alt="truck"
                                className="vehicle-planning-icon-size-truck"
                              />
                            </Col>
                            <Col
                              xxl={10}
                              xl={10}
                              lg={10}
                              md={10}
                              sm={10}
                              xs={10}
                              className="p-0"
                            >
                              <div className="div-flex-row">
                                <div className="div-flex-column vehicle-planning-item-detail">
                                  <span className="text-sm">
                                    Vehicle Number
                                  </span>
                                  <span
                                    className={`${isVehicleEditable ? 'text-md text-semi-bold text-blue text-underline' : 'text-md text-semi-bold text-grey'}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                      if (!isVehicleEditable) return;
                                      handleMoveToUpdatePlanning(
                                        vehicleTrackingData?.vehicle_tracking_number,
                                      );
                                    }}
                                  >
                                    {vehicleTrackingData.vehicle_number ||
                                      'N/A'}
                                  </span>
                                </div>

                                <div className="div-flex-column vehicle-planning-item-detail">
                                  <span className="text-sm">
                                    Transporters name
                                  </span>
                                  <span className="text-sm text-semi-bold text-grey">
                                    {vehicleTrackingData.transporter_name ||
                                      'N/A'}
                                  </span>
                                </div>

                                <div className="div-flex-column vehicle-planning-item-detail">
                                  <span className="text-sm">Plant</span>
                                  <span className="text-sm text-semi-bold text-grey">
                                    {vehicleTrackingData.plant || 'N/A'}
                                  </span>
                                </div>

                                <div className="div-flex-column vehicle-planning-item-detail">
                                  <span className="text-sm">Workflow</span>
                                  <span className="text-sm text-semi-bold text-grey">
                                    {vehicleTrackingData.workflow.name || 'N/A'}
                                  </span>
                                </div>

                                <div className="div-flex-column vehicle-planning-item-detail">
                                  <span className="text-sm">Tare Weight</span>
                                  <span className="text-sm text-semi-bold text-grey">
                                    {vehicleTrackingData.tare_weight
                                      ? `${vehicleTrackingData.tare_weight} KG`
                                      : 'N/A'}
                                  </span>
                                </div>

                                <div className="div-flex-column vehicle-planning-item-detail">
                                  <span className="text-sm">
                                    Tare Weight Time
                                  </span>
                                  <span className="text-sm text-semi-bold text-grey">
                                    {vehicleTrackingData.tare_weight_created_date_time ||
                                      'N/A'}
                                  </span>
                                </div>

                                <div className="div-flex-column vehicle-planning-item-detail">
                                  <span className="text-sm">Vehicle Entry</span>
                                  <span className="text-sm text-semi-bold text-grey">
                                    {vehicleTrackingData.created_date || 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            ) : (
              <div className="empty-state-container">
                <VehicleTrackingPlanningEmptyState className="vehicle-planning-empty-state-icon" />
                <div className="padding-top-10">
                  <span className="text-xxl text-bold text-grey">
                    No vehicle planned
                  </span>
                </div>
                <div>
                  <span className="text-md text-grey">
                    Click on the record vehicle below to plan your vehicle. Once
                    you add your vehicle <br /> you can plan your vehicles
                    outward process. You can proceed your vehicle form <br />{' '}
                    main gate, to loading and many more process.
                  </span>
                </div>
                <div className="icon-btn-none div-flex-center padding-top-10">
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handleMoveToCreateOutward}
                  >
                    <div className="div-align-center text-md">
                      Record Vehicle
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="footer-container">
            <div className="div-space-between div-align-center footer-content-for-page">
              <div className="div-flex-row cg-20 pagination-container div-align-center">
                <div className="div-flex-row cg-10 div-align-center">
                  <button
                    className="icon-btn-none"
                    aria-label="Previous page"
                    onClick={handlePreviousPage}
                  >
                    <ArrowLeft
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </button>
                  <input
                    className="page-number-container input-icon-remove text-sm"
                    value={formattedPageNumber}
                    onChange={handleInputChange}
                    type="number"
                    max={totalPages}
                  />
                  <button
                    className="icon-btn-none"
                    aria-label="Next Page"
                    onClick={handleNextPage}
                  >
                    <ArrowRight
                      className="icon-size-sm"
                      color={getColorCode(themeColor)}
                    />
                  </button>
                </div>
                <span className="text-grey text-sm">
                  Page {pageNumber} of {totalPages}
                </span>
              </div>
            </div>
          </div>
        </Col>
        {showFunnel && (
          <Col
            xxl={showFunnel ? 2 : 0}
            xl={showFunnel ? 3 : 0}
            lg={showFunnel ? 4 : 0}
            md={showFunnel ? 4 : 0}
            sm={showFunnel ? 12 : 0}
            xs={showFunnel ? 12 : 0}
            className="border-left border-bottom"
          >
            <div className="filter-list-item border-bottom">
              <span className="text-sm text-semi-bold">Filter</span>
            </div>
            <div className="div-flex-column div-space-between rg-20">
              <div className="filter-list-item-container-scroll div-flex-column">
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Transporter</span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti={false}
                      name="transporterId"
                      className="multi-select-input-section"
                      onChange={(selectedOption) =>
                        handleFilterInputChange({
                          target: {
                            name: 'transporterId',
                            value: selectedOption,
                          },
                        })
                      }
                      value={filterFormData.transporterId || ''}
                      options={transportersDataOption}
                      classNamePrefix="react-select"
                      placeholder="Search Transporter Name"
                      onFocus={() =>
                        dispatch(
                          getTransportersDropdownOptions(
                            tenantOrganisationId,
                            transporterSearchTerm,
                          ),
                        )
                      }
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change') {
                          setTransporterSearchTerm(inputValue);
                          dispatch(
                            getTransportersDropdownOptions(
                              tenantOrganisationId,
                              inputValue,
                            ),
                          );
                        }
                      }}
                    />
                    <span className="form-input-section-icon">
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Plant</span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti
                      name="plantId"
                      className="multi-select-input-section"
                      onChange={(selectedOption) =>
                        handleFilterInputChange({
                          target: {
                            name: 'plantId',
                            value: selectedOption,
                          },
                        })
                      }
                      value={filterFormData.plantId || null}
                      options={plantDataOptions}
                      classNamePrefix="react-select"
                      placeholder="Search Plant"
                      onFocus={() => dispatch(getPlants(tenantOrganisationId))}
                    />
                    <span className="form-input-section-icon">
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                </div>

                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Workflow</span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti={false}
                      name="workflowId"
                      className="multi-select-input-section"
                      onChange={(selectedOption) =>
                        handleFilterInputChange({
                          target: {
                            name: 'workflowId',
                            value: selectedOption,
                          },
                        })
                      }
                      value={filterFormData.workflowId || ''}
                      options={workflowDataOptions}
                      classNamePrefix="react-select"
                      placeholder="Search Workflow"
                      onFocus={() =>
                        dispatch(
                          getVehicleTrackingWorkflows(
                            tenantOrganisationId,
                            '2',
                          ),
                        )
                      }
                    />
                    <span className="form-input-section-icon">
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                </div>

                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Type</span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti={false}
                      name="typeId"
                      className="multi-select-input-section"
                      onChange={(selectedOption) =>
                        handleFilterInputChange({
                          target: {
                            name: 'typeId',
                            value: selectedOption,
                          },
                        })
                      }
                      value={filterFormData.typeId || ''}
                      options={vehicleTypeDataOptions}
                      classNamePrefix="react-select"
                      placeholder="Search Type"
                    />
                    <span className="form-input-section-icon">
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Capacity</span>
                  <div className="div-flex-row cg-10">
                    <div className="div-flex-column rg-5">
                      <span className="text-sm text-semi-bold">From</span>
                      <input
                        name="capacityFrom"
                        className="form-input-section"
                        size="small"
                        type="number"
                        value={filterFormData?.capacityFrom || ''}
                        onChange={handleFilterInputChange}
                      />
                    </div>
                    <div className="div-flex-column rg-5">
                      <span className="text-sm text-semi-bold">To</span>
                      <input
                        name="capacityTo"
                        className="form-input-section"
                        size="small"
                        type="number"
                        value={filterFormData?.capacityTo || ''}
                        onChange={handleFilterInputChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Unit</span>
                  <div className="input-wrapper">
                    <FilterMultiSelect
                      isMulti={false}
                      name="unitId"
                      className="multi-select-input-section"
                      onChange={(selectedOption) =>
                        handleFilterInputChange({
                          target: {
                            name: 'unitId',
                            value: selectedOption,
                          },
                        })
                      }
                      value={filterFormData.unitId || ''}
                      options={unitOfMeasurementOptions}
                      classNamePrefix="react-select"
                      placeholder="Search Unit"
                      onFocus={() =>
                        dispatch(
                          getUnitOfMeasurements(
                            tenantOrganisationId,
                            unitSearchTerm,
                          ),
                        )
                      }
                      onInputChange={(inputValue, { action }) => {
                        if (action === 'input-change') {
                          setUnitSearchTerm(inputValue);
                          dispatch(
                            getUnitOfMeasurements(
                              tenantOrganisationId,
                              inputValue,
                            ),
                          );
                        }
                      }}
                    />
                    <span className="form-input-section-icon">
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                        style={{ transform: 'rotate(90deg)' }}
                      />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column rg-5">
                  <span className="text-sm text-semi-bold">Start Date</span>
                  <div className="input-wrapper">
                    <input
                      name="startDate"
                      className="form-input-section input-icon-date"
                      size="small"
                      type="date"
                      value={filterFormData?.startDate || ''}
                      onChange={handleFilterInputChange}
                    />
                    <span className="form-input-section-icon">
                      <Calendar className="icon-size-sm" />
                    </span>
                  </div>
                </div>
                <div className="filter-list-item div-flex-column">
                  <div className="div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">End Date</span>
                    <div className="input-wrapper">
                      <input
                        name="endDate"
                        className="form-input-section input-icon-date"
                        size="small"
                        type="date"
                        value={filterFormData?.endDate || ''}
                        onChange={handleFilterInputChange}
                      />
                      <span className="form-input-section-icon">
                        <Calendar className="icon-size-sm" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                <button
                  className={`common-btn-outlined border-${themeColor}`}
                  onClick={handleResetFilter}
                >
                  Reset
                </button>
                <button
                  className={`common-btn theme-background-${themeColor}`}
                  onClick={() => handleFilterSubmit()}
                >
                  Apply
                </button>
              </div>
            </div>
          </Col>
        )}
      </Row>

      <Modal
        show={onHoldModal.show}
        onHide={() => handleCloseOnHoldModal()}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-md text-semi-bold">
            {onHoldModal.isOnHold ? 'Remove Hold' : 'Put Vehicle On Hold'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="div-flex-column rg-10">
            <span className="text-sm">
              {onHoldModal.isOnHold
                ? 'Are you sure you want to remove the hold on this vehicle?'
                : 'Please provide a reason for putting this vehicle on hold.'}
            </span>
            {!onHoldModal.isOnHold && (
              <>
                <textarea
                  className="form-input-section"
                  rows={4}
                  placeholder="Enter reason"
                  value={onHoldModal.reason}
                  onChange={(e) =>
                    setOnHoldModal((prev) => ({
                      ...prev,
                      reason: e.target.value,
                      reasonError: '',
                    }))
                  }
                />
                {onHoldModal.reasonError && (
                  <span className="text-xs" style={{ color: 'red' }}>
                    {onHoldModal.reasonError}
                  </span>
                )}
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className={`common-btn-outlined border-${themeColor} text-sm`}
            onClick={() => handleCloseOnHoldModal()}
            disabled={onHoldModal.isSubmitting}
          >
            Cancel
          </button>
          <div
            role="button"
            tabIndex={0}
            className={`vehicle-tracking-detail-move-stage-btn div-align-center theme-background-${themeColor}`}
            onClick={() => handleOnHoldSubmit()}
            disabled={
              onHoldModal.isSubmitting ||
              (!onHoldModal.isOnHold && !onHoldModal.reason.trim())
            }
          >
            <span className="text-sm">
              {onHoldModal.isSubmitting
                ? 'Saving...'
                : onHoldModal.isOnHold
                  ? 'Remove Hold'
                  : 'Confirm Hold'}
            </span>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
